import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { APIKit } from "config/axios";
import { ServerConstants } from "constant";

const { API_V1_USERS, API_V1_TODO } = ServerConstants;

export interface UserOption {
  id: number;
  fullName: string;
}

interface UserApiResponse {
  id: number;
  fullName?: string;
  firstName?: string;
  lastName?: string;
}

interface UseUserOptionsProps {
  opened: boolean;
  onModalClose?: () => void;
  preselectedClient?: UserOption;
}

export function useUserOptions({ opened, onModalClose, preselectedClient }: UseUserOptionsProps) {
  const { t } = useTranslation();
  const [employeeOptions, setEmployeeOptions] = useState<UserOption[]>([]);
  const [clientOptions, setClientOptions] = useState<UserOption[]>([]);

  // Helper function to map user API response to UserOption
  const mapUserToOption = useCallback(
    (u: UserApiResponse): UserOption => ({
      id: u.id,
      fullName:
        u.fullName ||
        `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
        t("todoList.common.unknownUser"),
    }),
    [t],
  );

  // Fetch employees and clients when modal opens
  useEffect(() => {
    if (!opened) {
      onModalClose?.();
      return;
    }

    const fetchUsers = async () => {
      try {
        const [employeesResponse, clientsResponse] = await Promise.all([
          APIKit.get(`${API_V1_USERS}?type=EMPLOYEE`),
          APIKit.get(`${API_V1_TODO}/clients`),
        ]);

        const employeesData: UserApiResponse[] =
          employeesResponse.data?.content || employeesResponse.data || [];
        const clientsData: UserApiResponse[] =
          clientsResponse.data?.content || clientsResponse.data || [];

        setEmployeeOptions(employeesData.map(mapUserToOption));
        setClientOptions(clientsData.map(mapUserToOption));
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, [opened, mapUserToOption, onModalClose]);

  // Memoize select options to avoid recreating on every render
  const employeeSelectOptions = useMemo(
    () => employeeOptions.map((u) => ({ value: String(u.id), label: u.fullName })),
    [employeeOptions],
  );

  const clientSelectOptions = useMemo(() => {
    const options = clientOptions.map((u) => ({ value: String(u.id), label: u.fullName }));

    if (!preselectedClient) {
      return options;
    }

    const selectedOption = {
      value: String(preselectedClient.id),
      label: preselectedClient.fullName,
    };

    return [
      selectedOption,
      ...options.filter((option) => option.value !== selectedOption.value),
    ];
  }, [clientOptions, preselectedClient]);

  return {
    employeeSelectOptions,
    clientSelectOptions,
  };
}
